import { Box, Button, Grid } from '@mui/material';
import Icon from '@mui/material/Icon';
import SensorsSelect from 'layouts/OccupancyAverages/components/SensorsSelect.js';
import { isEqual } from 'lodash';
import { useEffect, useCallback, useRef, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { brandColors } from 'types/colors';
import {
  initializeFiltersForUser,
  fetchDistributorCustomers,
  setSelectedDistributor,
  setSelectedCustomer,
  setSelectedBuilding,
  setSelectedFloor,
  setCurrentPage,
  getUsers,
  getBuildings,
} from 'stores/sensor/sensorStore';

import { fetchCurrentUser, fetchUserDevices } from 'stores/sensor/sensorStore';
import DropdownMenuVertical from '../DropdownMenuVertical';

import { generateFilterBarOptions, convertToLegacyFormat } from 'helpers/filterHelpers';
import { current } from '@reduxjs/toolkit';

// NOT IN USE ANYMORE
// NOT IN USE ANYMORE
// NOT IN USE ANYMORE

const HomePageFilterBar = props => {
  // ===== COMPONENT DEFINITION & BASIC SETUP =====
  const dispatch = useDispatch();
  const location = useLocation();
  const { control } = useForm({ defaultValues: {} });

  // ===== REDUX STATE SELECTORS (GROUP BY SLICE) =====
  const filterState = useSelector(state => state.persistence?.selectedFilterObject);
  const loading = useSelector(state => state.persistence?.loading);
  const users = useSelector(state => state.users.users);
  const usersLoading = useSelector(state => state.users.loading);
  const buildings = useSelector(state => state.users.buildings);
  // Sensors state (user, devices, etc.)
  const currentUser = useSelector(state => state.sensors.currentUser);
  const devices = useSelector(state => state.sensors.devices);
  const filteredDevices = useSelector(state => state.sensors.filteredDevices);
  const selectedSensors = useSelector(state => state.sensors.selectedSensors, isEqual);

  // Destructure filter values
  const { distributor, customer, building, floor } = filterState || {
    floor: 'all',
  };

  // ===== COMPUTED STATE (USEMEMO) =====
  // useMemo for fast future processing

  // Device-based filter options
  const filterOptions = useMemo(() => {
    return generateFilterBarOptions(
      users,
      devices,
      currentUser,
      { distributor, customer, building, floor },
      buildings,
      false, // take out 'all' option
    );
  }, [users, devices, currentUser, distributor, customer, building, floor, buildings]);

  // Format dropdowns
  const processedDistributorOptions = useMemo(() => {
    return convertToLegacyFormat(filterOptions.distributors || []).filter(option => option.value !== 'all'); // Remove "All" option
  }, [filterOptions.distributors]);

  const processedCustomerOptions = useMemo(() => {
    return convertToLegacyFormat(filterOptions.customers || []).filter(option => option.value !== 'all'); // Remove "All" option
  }, [filterOptions.customers]);

  const processedBuildingOptions = useMemo(() => {
    return convertToLegacyFormat(filterOptions.buildings || []).filter(option => option.value !== 'all'); // Remove "All" option
  }, [filterOptions.buildings]);

  const processedFloorOptions = useMemo(() => {
    return convertToLegacyFormat(filterOptions.floors || []).map(option => ({
      ...option,
      name: option.name.replace(/^floor\s*/i, ''), // Remove "Floor " prefix
    }));
  }, [filterOptions.floors]);

  const showDistributor = currentUser?.role === 's';
  const showCustomer = currentUser?.role === 's' || currentUser?.role === 'd';
  const showFloor = true;
  const buildingFilterWidth =
    currentUser?.role === 'c'
      ? { xs: '270px', sm: '380px', md: '460px' }
      : '270px';

  // ===== 5. EFFECTS - SIMPLIFIED INITIALIZATION =====
  // Navigation tracking
  useEffect(() => {
    const pathArr = location.pathname.split('/');
    const pageName = pathArr[1] || 'unknown';
    dispatch(setCurrentPage(pageName));
  }, [location.pathname, dispatch]);

  // Redux-based initialization - replaces all the old auto-selection logic
  useEffect(() => {
    if (!currentUser?.role) return;
    const initializeData = async () => {
      // Load basic data that ALL roles need
      await dispatch(getUsers());
      await dispatch(getBuildings());
      await dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
    };

    initializeData();
  }, [dispatch, currentUser?.role]);

  //Auto-selection only after data is loaded
  useEffect(() => {
    if (!currentUser?.role || !users?.length || !buildings?.length) return;

    // Only auto-select if NO existing values (fresh login)
    const hasExistingFilters = distributor || customer || building;

    if (hasExistingFilters !== '' && hasExistingFilters !== 'all') {
      return;
    }

    // Apply role-based defaults
    if (currentUser.role === 'd') {
      dispatch(setSelectedDistributor(currentUser.user_id));
      dispatch(fetchDistributorCustomers(currentUser.user_id));
    } else if (currentUser.role === 'c') {
      const customerIdStr = (currentUser.user_id || currentUser.id)?.toString();
      dispatch(setSelectedCustomer(customerIdStr));
    } else if (currentUser.role === 's') {
      if (processedDistributorOptions?.length > 0) {
        const firstDistributor = processedDistributorOptions[0];
        dispatch(setSelectedDistributor(firstDistributor.value));
        dispatch(fetchDistributorCustomers(firstDistributor.value));
      }
    }
  }, [dispatch, currentUser?.role, users?.length, buildings?.length, distributor, customer, building]);

  // STEP 3: Auto-select first customer for distributors
  useEffect(() => {
    if (
      (currentUser?.role === 'd' || currentUser?.role === 's') &&
      processedCustomerOptions?.length > 0 &&
      (!customer || customer === '')
    ) {
      const firstCustomer = processedCustomerOptions[0];
      dispatch(setSelectedCustomer(firstCustomer.value));
    }
  }, [currentUser?.role, processedCustomerOptions?.length, customer, dispatch]);

  // STEP 4: Auto-select first building when customer is selected
  useEffect(() => {
    if (processedBuildingOptions?.length > 0 && (!building || building === '')) {
      const shouldAutoSelect =
        currentUser?.role === 'c' || (currentUser?.role === 'd' && customer) || (currentUser?.role === 's' && customer);

      if (shouldAutoSelect) {
        const firstBuilding = processedBuildingOptions[0];
        dispatch(setSelectedBuilding(firstBuilding.value));
      }
    }
  }, [processedBuildingOptions?.length, building, customer, currentUser?.role, dispatch]);

  // ===== 6. EVENT HANDLERS (GROUP BY HIERARCHY) =====
  // Distributor/Customer handlers
  const handleDistributorChange = useCallback(
    e => {
      const distId = e.target.value;
      dispatch(setSelectedDistributor(distId));

      if (distId && distId !== '') {
        dispatch(fetchDistributorCustomers(distId));
      }
    },
    [dispatch],
  );

  const handleCustomerChange = useCallback(
    e => {
      const custId = e.target.value;
      dispatch(setSelectedCustomer(custId));

      // Auto-select first building after customer selection
      setTimeout(() => {
        const selectedCustomer = users.find(user => user.id.toString() === custId);
        if (selectedCustomer?.building_permissions && selectedCustomer.building_permissions.length > 0) {
          const firstBuilding = selectedCustomer.building_permissions[0];
          dispatch(setSelectedBuilding(firstBuilding.building_id.toString()));
        }
      }, 0);
    },
    [dispatch, users],
  );

  // Location handlers
  const handleBuildingChange = useCallback(
    e => {
      const buildingId = e.target.value;
      dispatch(setSelectedBuilding(buildingId));
    },
    [dispatch],
  );

  const handleFloorChange = useCallback(
    e => {
      const floorValue = e.target.value;
      dispatch(setSelectedFloor(floorValue));
    },
    [dispatch],
  );

  // Other handlers
  const handleSearchClick = useCallback(
    async e => {
      // Validation for pages that require sensor selection
      if (!props.homePage && selectedSensors.length === 0 && props.insightsPage !== true && props.heatmapPage !== true) {
        alert('No sensors selected.');
        return;
      }

      // Call parent callback with current filter values
      if (props.onSearchClick) {
        props.onSearchClick(customer, building, floor);
      }
    },
    [props, selectedSensors, building, floor, customer],
  );

  // ===== 7. COMPONENT LOGIC & HELPERS =====
  // Disabled logic

  const floorDisabled = !building || building === '' || processedFloorOptions?.length <= 1;

  // ===== 8. RENDER/JSX =====
  return (
    <Box>
      <form>
        <Grid
          container
          spacing={1}
          sx={{
            width: 'fit-content',
            height: '70px',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          {/* ===== DISTRIBUTOR DROPDOWN (Super Admin Only) ===== */}
          {showDistributor && (
            <Grid item sx={{ width: '182px' }}>
              <Box py={1}>
                <Controller
                  name="distributor"
                  control={control}
                  render={() => (
                    <DropdownMenuVertical
                      disabled={loading.distributors}
                      options={processedDistributorOptions || []}
                      handleChange={handleDistributorChange}
                      selectedOption={distributor}
                      labelName="Distributor"
                      placeholder="Select Distributor"
                    />
                  )}
                />
              </Box>
            </Grid>
          )}

          {/* ===== CUSTOMER DROPDOWN (Super Admin & Distributors) ===== */}
          {showCustomer && (
            <Grid item sx={{ width: '182px' }}>
              <Box py={1}>
                <Controller
                  name="customer"
                  control={control}
                  render={() => (
                    <DropdownMenuVertical
                      options={processedCustomerOptions || []}
                      handleChange={handleCustomerChange}
                      selectedOption={customer}
                      labelName="Customer"
                      placeholder="Select Customer"
                    />
                  )}
                />
              </Box>
            </Grid>
          )}

          {/* ===== BUILDING DROPDOWN (All Roles) ===== */}
          <Grid item sx={{ width: buildingFilterWidth, maxWidth: buildingFilterWidth, minWidth: buildingFilterWidth }}>
            <Box py={1}>
              <Controller
                name="building"
                control={control}
                render={() => (
                  <DropdownMenuVertical
                    options={processedBuildingOptions}
                    handleChange={handleBuildingChange}
                    selectedOption={building}
                    labelName="Building"
                    placeholder="Select Building"
                    width={buildingFilterWidth}
                  />
                )}
              />
            </Box>
          </Grid>

          {/* ===== FLOOR DROPDOWN ===== */}
          {showFloor && (
            <Grid item sx={{ width: '100px' }}>
              <Box py={1}>
                <Controller
                  name="floor"
                  control={control}
                  render={() => (
                    <DropdownMenuVertical
                      disabled={building === 'all'}
                      options={processedFloorOptions}
                      handleChange={handleFloorChange}
                      selectedOption={floor}
                      labelName="Floor"
                      placeholder="All"
                    />
                  )}
                />
              </Box>
            </Grid>
          )}

          {/* ===== SENSORS SELECTOR ===== */}
          {props.sensorsSelect && (
            <SensorsSelect
              disabled={building === 'all'}
              selectedCustomerId={customer}
              selectedBuildingId={building}
              selectedFloor={floor}
            />
          )}

          {/* ===== SEARCH/REFRESH BUTTON ===== */}
          <Grid>
            <Button
              sx={{
                backgroundColor: brandColors.LIGHT_BLUE,
                color: '#000',
                height: '3rem',
                marginTop: '7px',
                marginLeft: '7px',
                border: `1px solid ${brandColors.BLUE}`,
                '&:hover': {
                  backgroundColor: `${brandColors.BLUE}`,
                  color: '#fff',
                },
              }}
              variant="gradient"
              onClick={handleSearchClick}
              disabled={false}>
              {props.isRefreshBtn ? (
                <Icon color="inherit" fontSize="large">
                  sync
                </Icon>
              ) : (
                <Icon color="inherit" fontSize="medium">
                  search
                </Icon>
              )}
            </Button>
          </Grid>

          {/* ===== LOADING INDICATOR ===== */}
          {(loading.distributors || loading.customers || loading.buildings) && (
            <Grid item>
              <Box sx={{ color: brandColors.BLUE, fontSize: '0.8rem', ml: 1 }}>Loading...</Box>
            </Grid>
          )}
        </Grid>
      </form>
    </Box>
  );
};

export default HomePageFilterBar;
